import { cloneDeep } from "lodash-es";
import type {
  MenuItem,
  StructuredTopology,
  TopologyKind,
  SplitByLayerItemsProps,
  SplitByLayerItemsResult,
  StructuredTopologyArrayKeys,
  ActionsByGroups,
  Action,
} from "interfaces/product/topology/topology.types";

export const topologyItems = {
  system_controller: {
    sameLayer: ["system_controller", "control_panel", "component"],
    connectedByTopBracketChildren: ["fam"],
    iconName: "tech-symbol-cm",
    bgColor: "bg-gray-700",
    borderColor: "border-gray-700",
  },
  control_panel: {
    iconName: "tech-symbol-panel",
    bgColor: "bg-gray-700",
    borderColor: "border-gray-700",
  },
  switch: {
    sameLayer: ["component"],
    connectedByTopBracketChildren: ["fam"],
    iconName: "tech-symbol-switch",
  },
  fam: {
    connectedByTopBracketChildren: ["fam"],
    rightPlacedChildren: ["component"],
    bottomPlacedChildren: ["splitter", "distributor"],
    chainedChildren: ["module", "can"],
    iconName: "tech-symbol-fam",
    bgColor: "bg-gray-500",
    borderColor: "border-gray-500",
    width: "208px",
  },
  module: {
    sameLayer: ["module", "can"],
    iconName: "tech-symbol-cm",
    bgColor: "bg-gray-400",
    borderColor: "border-gray-400",
  },
  splitter: {
    sameLayer: [],
    width: "144px",
    iconName: "tech-symbol-cm",
  },
  distributor: {
    sameLayer: [],
    width: "144px",
    iconName: "tech-symbol-cm",
  },
  component: {
    iconName: "tech-symbol-fd",
    directlyConnected: ["component"],
    sameLayer: [],
  },
  can: {
    iconName: "tech-symbol-cm",
    directlyConnected: ["can"],
  },
};

const splitByLayerItems = ({
  kind,
  configurations = [],
}: SplitByLayerItemsProps): SplitByLayerItemsResult => {
  const sameLayer: StructuredTopology[] = [];
  const rightPlacedChildren: StructuredTopology[] = [];
  const bottomPlacedChildren: StructuredTopology[] = [];
  const chainedChildren: StructuredTopology[] = [];
  const connectedByTopBracketChildren: StructuredTopology[] = [];
  const directlyConnected: StructuredTopology[] = [];
  let isCBModule = false;
  let isLastFAM = false;

  switch (kind) {
    case "can":
      configurations?.forEach((item) => {
        if (item.title === "CB") {
          isCBModule = true;

          item.configurations?.forEach((cbItem) => {
            rightPlacedChildren.push({ ...cbItem, isSBItem: true });
          });

          return;
        }

        if (topologyItems[kind]?.directlyConnected?.includes(item.kind)) {
          directlyConnected.push(item);
          return;
        }

        const config = topologyItems[kind];
        if (
          config &&
          "sameLayer" in config &&
          (config.sameLayer as string[]).includes(item.kind)
        ) {
          sameLayer.push(item);
          return;
        }

        rightPlacedChildren.push(item);
      });
      break;
    case "fam":
      configurations?.forEach((item) => {
        if (
          topologyItems[kind].connectedByTopBracketChildren.includes(item.kind)
        ) {
          connectedByTopBracketChildren.push(item);
          return;
        }
        if (topologyItems[kind].rightPlacedChildren.includes(item.kind)) {
          rightPlacedChildren.push(item);
          return;
        }
        if (topologyItems[kind].bottomPlacedChildren.includes(item.kind)) {
          bottomPlacedChildren.push(item);
          return;
        }
        if (topologyItems[kind].chainedChildren.includes(item.kind)) {
          chainedChildren.push(item);
        }
      });
      break;
    case "system_controller":
    case "switch":
      configurations?.forEach((item) => {
        if (topologyItems[kind].sameLayer.includes(item.kind)) {
          sameLayer.push(item);
          return;
        }
        if (
          topologyItems[kind].connectedByTopBracketChildren.includes(item.kind)
        ) {
          connectedByTopBracketChildren.push(item);
          return;
        }
        bottomPlacedChildren.push(item);
      });
      break;
    case "component":
      configurations?.forEach((item) => {
        if (topologyItems[kind].directlyConnected.includes(item.kind)) {
          directlyConnected.push(item);
        }
      });
      break;
    default: {
      const config = topologyItems[kind as keyof typeof topologyItems];
      configurations?.forEach((item) => {
        if (
          config &&
          "sameLayer" in config &&
          (config.sameLayer as string[]).includes(item.kind)
        ) {
          sameLayer.push(item);
        } else {
          rightPlacedChildren.push(item);
        }
      });
      break;
    }
  }

  if (kind === "fam" && connectedByTopBracketChildren.length === 0) {
    isLastFAM = true;
  }

  return {
    sameLayer,
    connectedByTopBracketChildren,
    isCBModule,
    rightPlacedChildren,
    bottomPlacedChildren,
    chainedChildren,
    isLastFAM,
    directlyConnected,
  };
};

function convertObj(
  {
    configurations,
    kind,
    ...rest
  }: StructuredTopology = {} as StructuredTopology,
): StructuredTopology {
  const result = { kind, ...rest };

  const {
    sameLayer,
    isCBModule,
    rightPlacedChildren,
    bottomPlacedChildren,
    connectedByTopBracketChildren,
    chainedChildren,
    isLastFAM,
    directlyConnected,
  } = splitByLayerItems({
    kind: kind as TopologyKind,
    configurations: configurations as StructuredTopology[],
  });

  if (sameLayer.length > 0) {
    result.sameLayer = sameLayer.map((o) => convertObj(o));
  }
  if (isCBModule) {
    result.isCBModule = true;
  }
  if (rightPlacedChildren.length > 0) {
    result.rightPlacedChildren = rightPlacedChildren.map((o) => convertObj(o));
  }
  if (bottomPlacedChildren.length > 0) {
    result.bottomPlacedChildren = bottomPlacedChildren.map((o) =>
      convertObj(o),
    );
  }
  if (directlyConnected.length > 0) {
    result.directlyConnected = getFlatFromChild(
      directlyConnected.map((o) => convertObj(o)),
      "directlyConnected",
    );
  }
  if (chainedChildren.length > 0) {
    result.chainedChildren = getFlatFromChild(
      chainedChildren.map((o) => convertObj(o)),
      "sameLayer",
    );
  }
  if (connectedByTopBracketChildren.length > 0) {
    result.connectedByTopBracketChildren = getFlatFromChild(
      connectedByTopBracketChildren.map((o) => convertObj(o)),
      "connectedByTopBracketChildren",
    );
  }
  if (isLastFAM) {
    result.isLastFAM = isLastFAM;
  }

  return result;
}

function getFlatFromChild(
  arr: StructuredTopology[],
  prop: StructuredTopologyArrayKeys,
) {
  const result: StructuredTopology[] = [];

  arr.forEach((obj) => {
    result.push(obj);
    const children = obj[prop];
    if (children && Array.isArray(children)) {
      result.push(...getFlatFromChild(children, prop));
      delete obj[prop];
    }
  });

  return result;
}

export const getTopology = (data: StructuredTopology) => {
  return convertObj(data);
};

export const convertFAMs = (arr: StructuredTopology[]) => {
  const result = [];
  let group = [];
  for (let i = 0; i < arr.length; i += 1) {
    group.push({ ...arr[i], zoneIdx: i + 1 });
    if (Object.prototype.hasOwnProperty.call(arr[i], "isLastFAM")) {
      result.push(group);
      group = [];
    }
  }
  if (group.length > 0) {
    result.push(group);
  }
  return result;
};

const actions = [
  {
    id: 10,
    title: "+FAM-E",
    action: "addComponent",
    handler: (item: MenuItem, acc: ActionsByGroups) => {
      acc.addFAMLayerOptions = item.submenu || [{ id: 0 }];
    },
  },
  {
    id: 11,
    // title: "+ Module",
    // action: "addComponent",
    handler: (item: MenuItem, acc: ActionsByGroups) => {
      acc.addCMLayerOptions = item.submenu || [];
    },
  },
  {
    id: 12,
    // title: "+ Component",
    // action: "addComponent",
    handler: (item: MenuItem, acc: ActionsByGroups) => {
      acc.addComponentsLayerOptions = item.submenu || [];
    },
  },
  {
    id: 9,
    // title: "Hier einfügen",
    // action: "pasteHere",
    handler: (item: MenuItem, acc: ActionsByGroups) => {
      if (item?.highlight) {
        acc.isPasteEnabled = item.highlight;
      }
    },
  },
  {
    id: 6,
    // title: "Konfiguration übernehmen",
    action: "pasteConfigEnabled",
    handler: (item: MenuItem, acc: ActionsByGroups) => {
      if (item?.highlight) {
        acc.isPastConfigEnabled = item.highlight;
      }
    },
  },
];

const actionMap = actions.reduce<Record<number, Action>>((map, action) => {
  map[action.id] = action;
  return map;
}, {});

export const getValidActions = (menu: MenuItem[]) => {
  if (menu) {
    const res = menu.reduce(
      (acc, item) => {
        if (item.enabled && actionMap[item.id]) {
          const action = actionMap[item.id];
          action.handler(item, acc);
        }
        return acc;
      },
      {
        addCMLayerOptions: [],
        addComponentsLayerOptions: [],
        addFAMLayerOptions: [],
        isPasteEnabled: false,
        isPastConfigEnabled: false,
      },
    );
    return res;
  }
  return {
    addCMLayerOptions: [],
    addComponentsLayerOptions: [],
    addFAMLayerOptions: [],
    isPasteEnabled: false,
    isPastConfigEnabled: false,
  };
};

export const normalizeCanSiblingsForFireProtection = (
  root: StructuredTopology,
) => {
  const clonedRoot = cloneDeep(root);
  const walk = (node: StructuredTopology) => {
    if (!node || typeof node !== "object") {
      return;
    }

    (Object.keys(node) as (keyof StructuredTopology)[]).forEach((k) => {
      const v = node[k];

      if (!Array.isArray(v)) {
        return;
      }

      if (node.kind === "fam" && k === "chainedChildren" && v.length) {
        for (let i = 0; i < v.length; i += 1) {
          const cm = (v as StructuredTopology[])[i];
          if (
            cm?.kind === "can" &&
            Array.isArray(cm.directlyConnected) &&
            cm.directlyConnected.length
          ) {
            const moved = cm.directlyConnected.length;
            v.splice(i + 1, 0, ...cm.directlyConnected);
            cm.directlyConnected = [];
            i += moved;
          }
        }
      }

      (v as StructuredTopology[]).forEach(walk);
    });
  };

  walk(clonedRoot);
  return clonedRoot;
};

const FINAL_WIZARD_DISABLED_STEPS = {
  roomcontrol: [2, 3],
  fireprotection: [2, 3, 4],
  roomcontrolwireless: [2],
};

export const isFinalWizardStepDisabled = (
  idx: number,
  topologyStatus: string,
  subType: string,
  availableSteps: number,
) => {
  if (idx <= 1) return false;
  if (topologyStatus === "Final") {
    const disabledSteps =
      FINAL_WIZARD_DISABLED_STEPS[
        subType as keyof typeof FINAL_WIZARD_DISABLED_STEPS
      ] ?? [];
    return disabledSteps.includes(idx);
  }
  return idx > availableSteps;
};
